import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Users, DollarSign, Store, Calendar, Globe, Utensils,
  ChevronRight, Check, ArrowRight, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";

const DIETARY_OPTIONS = [
  "Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Keto",
  "Paleo", "Halal", "Kosher", "Nut-Free", "Low-Sodium", "None",
];

const ALLERGY_OPTIONS = [
  "Peanuts", "Tree Nuts", "Dairy", "Eggs", "Wheat/Gluten",
  "Soy", "Fish", "Shellfish", "Sesame",
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Monthly" },
];

const POPULAR_STORES = [
  "Walmart", "Kroger", "Aldi", "Costco", "Target", "Publix",
  "Safeway", "Meijer", "H-E-B", "Whole Foods", "Trader Joe's",
  "Food Lion", "ShopRite", "Wegmans", "Sam's Club", "Albertsons",
  "Lidl", "Sprouts", "WinCo", "Hy-Vee",
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "zh", label: "Chinese" },
  { value: "ko", label: "Korean" },
  { value: "vi", label: "Vietnamese" },
];

const ProfileOnboarding = () => {
  const { updateProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [dietary, setDietary] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [householdSize, setHouseholdSize] = useState("2");
  const [groceryBudget, setGroceryBudget] = useState("");
  const [preferredStores, setPreferredStores] = useState([]);
  const [customStore, setCustomStore] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [language, setLanguage] = useState("en");

  const toggleDietary = (opt) => {
    if (opt === "None") {
      setDietary(dietary.includes("None") ? [] : ["None"]);
    } else {
      setDietary(prev =>
        prev.filter(d => d !== "None").includes(opt)
          ? prev.filter(d => d !== opt)
          : [...prev.filter(d => d !== "None"), opt]
      );
    }
  };

  const toggleStore = (store) => {
    setPreferredStores(prev =>
      prev.includes(store) ? prev.filter(s => s !== store) : [...prev, store]
    );
  };

  const toggleAllergy = (allergy) => {
    setAllergies(prev =>
      prev.includes(allergy) ? prev.filter(a => a !== allergy) : [...prev, allergy]
    );
  };

  const addCustomStore = () => {
    const name = customStore.trim();
    if (name && !preferredStores.includes(name)) {
      setPreferredStores(prev => [...prev, name]);
    }
    setCustomStore("");
  };

  const handleFinish = async () => {
    setSaving(true);
    await updateProfile({
      dietary_preferences: dietary.filter(d => d !== "None"),
      allergies,
      city: city.trim(),
      state: state.trim(),
      zip_code: zipCode.trim(),
      household_size: parseInt(householdSize) || 1,
      grocery_budget: parseFloat(groceryBudget) || 0,
      preferred_stores: preferredStores,
      shopping_frequency: frequency,
      language,
      profile_completed: true,
    });
    setSaving(false);
    navigate("/subscription");
  };

  const steps = [
    // Step 0: Dietary
    {
      title: "Dietary Preferences",
      subtitle: "Help us personalize your meal plans and recipes",
      icon: Utensils,
      content: (
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map(opt => (
            <button
              key={opt}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                dietary.includes(opt)
                  ? "bg-green-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => toggleDietary(opt)}
              data-testid={`dietary-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {dietary.includes(opt) && <Check className="h-3 w-3 inline mr-1" />}
              {opt}
            </button>
          ))}
        </div>
      ),
    },
    // Step 1: Allergies
    {
      title: "Food Allergies",
      subtitle: "We'll exclude recipes with these allergens",
      icon: X,
      content: (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {ALLERGY_OPTIONS.map(allergy => (
              <button
                key={allergy}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  allergies.includes(allergy)
                    ? "bg-red-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => toggleAllergy(allergy)}
                data-testid={`allergy-${allergy.toLowerCase().replace(/[\s/]+/g, "-")}`}
              >
                {allergies.includes(allergy) && <Check className="h-3 w-3 inline mr-1" />}
                {allergy}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400">Select any that apply. You can update these later in Settings.</p>
        </div>
      ),
    },
    // Step 1: Location
    {
      title: "Your Location",
      subtitle: "We'll find the best local store deals for you",
      icon: MapPin,
      content: (
        <div className="space-y-3">
          <Input placeholder="City" value={city} onChange={e => setCity(e.target.value)} data-testid="city-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="State" value={state} onChange={e => setState(e.target.value)} data-testid="state-input" />
            <Input placeholder="Zip Code" value={zipCode} onChange={e => setZipCode(e.target.value)} data-testid="zip-input" />
          </div>
        </div>
      ),
    },
    // Step 2: Household & Budget
    {
      title: "Household & Budget",
      subtitle: "Optimize meal plans and budgets for your household",
      icon: Users,
      content: (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Household Size</label>
            <div className="flex gap-2">
              {["1", "2", "3", "4", "5", "6+"].map(s => (
                <button
                  key={s}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    householdSize === s ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() => setHouseholdSize(s)}
                  data-testid={`household-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Monthly Grocery Budget</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                placeholder="e.g. 600"
                value={groceryBudget}
                onChange={e => setGroceryBudget(e.target.value)}
                className="pl-9"
                data-testid="budget-input"
              />
            </div>
          </div>
        </div>
      ),
    },
    // Step 3: Stores & Frequency
    {
      title: "Shopping Habits",
      subtitle: "We'll match deals from your favorite stores",
      icon: Store,
      content: (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Preferred Stores</label>
            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
              {POPULAR_STORES.map(store => (
                <button
                  key={store}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    preferredStores.includes(store)
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() => toggleStore(store)}
                  data-testid={`store-${store.toLowerCase().replace(/[\s']+/g, "-")}`}
                >
                  {preferredStores.includes(store) && <Check className="h-2.5 w-2.5 inline mr-0.5" />}
                  {store}
                </button>
              ))}
            </div>
            {/* Custom store chips */}
            {preferredStores.filter(s => !POPULAR_STORES.includes(s)).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {preferredStores.filter(s => !POPULAR_STORES.includes(s)).map(store => (
                  <button
                    key={store}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500 text-white"
                    onClick={() => toggleStore(store)}
                    data-testid={`store-custom-${store.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Check className="h-2.5 w-2.5 inline mr-0.5" />{store} <X className="h-2.5 w-2.5 inline ml-0.5" />
                  </button>
                ))}
              </div>
            )}
            {/* Add custom store */}
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Add a local store..."
                value={customStore}
                onChange={e => setCustomStore(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomStore())}
                className="text-sm h-8"
                data-testid="custom-store-input"
              />
              <Button
                type="button"
                size="sm"
                className="bg-green-500 hover:bg-green-600 text-white h-8 px-3 text-xs"
                onClick={addCustomStore}
                disabled={!customStore.trim()}
                data-testid="add-store-btn"
              >
                Add
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Shopping Frequency</label>
            <div className="grid grid-cols-2 gap-2">
              {FREQUENCY_OPTIONS.map(f => (
                <button
                  key={f.value}
                  className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                    frequency === f.value ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() => setFrequency(f.value)}
                  data-testid={`freq-${f.value}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-testid="profile-onboarding">
      <div className="w-full max-w-sm space-y-5">
        {/* Progress */}
        <div className="flex items-center gap-1">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i <= step ? "bg-green-500" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* Step Card */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">{current.title}</h2>
                <p className="text-xs text-gray-500">{current.subtitle}</p>
              </div>
            </div>

            {current.content}

            <div className="flex gap-2 pt-2">
              {step > 0 && (
                <Button variant="outline" className="flex-1" onClick={() => setStep(step - 1)} data-testid="back-btn">
                  Back
                </Button>
              )}
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={isLast ? handleFinish : () => setStep(step + 1)}
                disabled={saving}
                data-testid="next-btn"
              >
                {saving ? "Saving..." : isLast ? "Finish Setup" : "Next"}
                {!saving && (isLast ? <Check className="h-4 w-4 ml-1" /> : <ArrowRight className="h-4 w-4 ml-1" />)}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Skip */}
        <button
          className="text-xs text-gray-400 hover:text-gray-500 block mx-auto"
          onClick={handleFinish}
          data-testid="skip-onboarding"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default ProfileOnboarding;
